import {
    Avatar,
    Button,
    useTheme,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fade,
    FormControlLabel,
    Grid,
    IconButton,
    Paper,
    Radio,
    Stack,
    TextField,
    Typography,
    Box,
    ToggleButtonGroup,
    ToggleButton,
    Checkbox,
    FormHelperText,
    ClickAwayListener,
    Tooltip
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { Order, OrderStatus, OrderType, Product, TransactionType, transactionTypeToJSON, Validity, Broker_Variety, Broker_OrderType, MarginFromBrokerResponse, MarginFromBrokerRequest_Order, MarginFromBrokerRequest_TradingAccount, MarginFromBrokerRequest, productToJSON } from '../../proto/generated/order'
import React, { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { selectMargins, selectPrimaryTradingAccount, selectTradingAccounts } from '../../selectors/user'
import { v4 as uuid } from 'uuid'
import { placeOrModifyOrder } from '../../services/apis/orders'
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import websocketActions from '../../actions/websocket.actions'
import { Instrument, SecurityCategory_CategoryCode, Segment } from '../../proto/generated/security'
import { LOGOS_BASE_URL } from '../../assets/constants'
import { getColorForSymbol } from '../../util/general'
import { selectOrdersHistory, selectQuickOrderData } from '../../selectors/orders'
import { Exchange, exchangeToJSON } from '../../proto/generated/common'
import { openConnectAccountDialog, QuickTradeData, removeQuickOrderData, setQuickOrderData, updateOrderHistory } from '../../reducer/orders.reducer'
import { fetchSecuritySnapshotsBulk } from '../../services/apis/candles'
import { PlaceOrModifyOrdersRequest, TradingAccountStatus } from '../../proto/generated/trading-bridge'
import { selectOrderBook, selectReferenceSecurityMap, selectSnapshots } from '../../selectors/security'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { blue, red } from '../../assets/colors'
import { useStyles } from './styles'
import ToggleSwitch from '../common/toggleSwitch/toggleSwitch'
import useDraggable from '../../hooks/useDraggable'
import { SurveillanceWarningDialog } from './RiskDisclaimationDialog'
import { LoadingButton } from '@mui/lab'
import { PopUpPopover } from './PopUpDialog'
import AlertIcon from '../../assets/icons/AlertIcon'
import { addOrderAlertMessage, getRoundedPrice } from '../../util/orders'
import TradingAccountSelector from '../tradingAccountStatus/TradingAccountSelector'
import QuickTradeProductTabs from './quickTradeProductTabs/quickTradeProductTabs'
import GenericFilterTabs from '../genericTabs/genericFilterTabs'
import { SlTargetType } from '../common/slTargetInput/slTargetInput'
import { getRequiredMarginFromBroker, syncAccountMarginFromBroker } from '../../services/apis/tradingAccount'
import { BrokerId, generateBrokerLoginUrl, keyValueArrayFromTradingAccountParams } from '../../util/tradingAccounts'
import { setMarginForAccount } from '../../reducer/user.reducer'
import RefreshIcon from '../../assets/icons/RefreshIcon'
import { debounce } from 'throttle-debounce'
import { setBatchId } from '../../reducer/strategyBuilder.reducer'
import OrderBookSelectionDialog from './OrderBookSelectionDialog'
import InfoIcon from '../../assets/icons/InfoIcon'
import { useUser } from '../../context/UserContext'
import { FeatureCategory } from '../adminUserRestrictions/Restrictions.constants'
import ROUTES from '../../assets/routes'
import { useNavigate } from 'react-router-dom'


const QuickTradeWindowV2 = () => {
    const dispatch = useDispatch()
    const theme = useTheme()
    const navigate = useNavigate()
    const { classes, cx } = useStyles()
    const tradeData = useSelector(selectQuickOrderData)
    const snapshots = useSelector(selectSnapshots);
    const {
        openDialog, mode, maxQuantity, tradingAccountId, isSquareOffOrder, type, addStopLoss, addTarget, stopLoss = 0, target = 0, stopLossType, targetType, order
    } = tradeData;

    const { security, transactionType, brokerOrderType, orderId,
        orderType, product, price, triggerPrice, quantity, validity = Validity.DAY, disclosedQuantity = 0, marketProtection = 0 } = order;
    const orderHistory = useSelector(selectOrdersHistory);
    const refSecurityMap = useSelector(selectReferenceSecurityMap)
    const [selectedSecurity, setSelectedSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>> | undefined>()
    const [availableExchanges, setAvailableExchanges] = useState<Exchange[]>([])
    useEffect(() => {
        if (!security?.exchange && !security?.code) {
            setSelectedSecurity(undefined)
            setAvailableExchanges([])
            return
        }
        const key = getSecurityKey(security.exchange, security.code)
        getSecurityAsync(key).then(async (sec) => {
            if (!sec) {
                setSelectedSecurity(undefined)
                setAvailableExchanges([])
                return
            }
            if (sec.derived) {
                const refKey = refSecurityMap[key]
                const refSec = await getSecurityAsync(refKey)
                setSelectedSecurity(refSec ?? sec)
            } else {
                setSelectedSecurity(sec)
            }
        })
    }, [security, refSecurityMap])
    useEffect(() => {
        if (!selectedSecurity?.code) {
            setAvailableExchanges([])
            return
        }
        Promise.all([Exchange.NSE, Exchange.BSE].map(e => getSecurityAsync(getSecurityKey(e, selectedSecurity.code)).then(sec => sec ? e : null))).then(([nse, bse]) => {
            setAvailableExchanges([nse, bse].filter((e): e is Exchange => e != null))
        })
    }, [selectedSecurity?.code])

    const selectedSecurityKey = useMemo(() => getSecurityKey(selectedSecurity?.exchange, selectedSecurity?.code), [selectedSecurity])
    const margins = useSelector(selectMargins)
    const orderBook = useSelector(selectOrderBook(selectedSecurityKey))
    const securitySnapshot = useMemo(() => snapshots[selectedSecurityKey], [snapshots, selectedSecurityKey])
    const snapshotRef = useRef(securitySnapshot);
    const tradingAccounts = useSelector(selectTradingAccounts)
    const selectedTradingAccount = useSelector(selectPrimaryTradingAccount)
    const primaryTradingAccount = useMemo(() => {
        if (type === 'modify' || isSquareOffOrder) {
            return tradingAccounts.find(t => t.accountId === tradingAccountId) ?? selectedTradingAccount
        }
        return selectedTradingAccount
    }, [selectedTradingAccount, tradingAccountId, tradingAccounts])
    const [anchorEl, setAnchorEl] = useState<{
        element: HTMLElement
        type: 'price' | 'triggerPrice'
    } | null>(null);
    const selectedSecurityRef = useRef('')
    const [isDragging, setIsDragging] = useState(false)
    const basketId = useMemo(() => {
        if (!selectedSecurity || !selectedTradingAccount) return null;
        return `default_${selectedTradingAccount.accountId}_${productToJSON(product)}_${selectedSecurity.code}_${exchangeToJSON(selectedSecurity.exchange)}`.toLowerCase();
    }, [selectedTradingAccount, selectedSecurity, product]);
    const batchId = useMemo(() => uuid(), []);
    const [clientOrderId, setClientOrderId] = useState(uuid());
    const [slClientOrderId, setSlClientOrderId] = useState(uuid());
    const [targetClientOrderId, setTargetClientOrderId] = useState(uuid());
    const [showSurveillanceDialog, setShowSurveillanceDialog] = useState(false);
    const [confirmedFromSurveillance, setConfirmedFromSurveillance] = useState(false)
    const [requestInProgres, setRequestInProgress] = useState(false)
    const [popUpOver, setpopUpOver] = useState(false);
    const anchorRef = useRef(null);
    const [advancedMode, setAdvancedMode] = useState(false)
    const renderedFirstTime = useRef(false)
    const modifyingOrder = useMemo(() => orderId && orderHistory?.find(o => o.orderId === orderId), [orderId, orderHistory])
    const stopLossInputRef = useRef<HTMLInputElement>(null)
    const targetInputRef = useRef<HTMLInputElement>(null)
    const { user: { hasAccess } } = useUser()
    const showAllTradeOptions = useMemo(() => {
        const isPaperTrading = selectedTradingAccount?.brokerCode === BrokerId.PAPER_TRADING
        const hasPaperTradingAccess = hasAccess(FeatureCategory.PAPER_TRADING_FULL_ACCESS)
        const result = !isPaperTrading || hasPaperTradingAccess
        return result
    }, [selectedTradingAccount, hasAccess])

    const primaryTradingAccountMarginAvailable = useMemo(() => {
        return margins[primaryTradingAccount?.accountId]?.marginAvailable
    }, [primaryTradingAccount, margins])
    const [requiredMargin, setRequiredMargin] = useState<MarginFromBrokerResponse>({
        totalMargin: 0,
        brokerCharges: undefined,
    })

    const basketOpenSlOrder = useMemo(() => {
        return (orderHistory || []).find(o => {
            return o.basketId === basketId && transactionType !== o.transactionType &&
                [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status) && o.orderType === OrderType.STOP_LOSS
        })
    }, [basketId, orderHistory, transactionType])

    const basketInactiveSlOrder = useMemo(() => {
        return (orderHistory || []).find(o => {
            return o.basketId === basketId && transactionType !== o.transactionType &&
                o.status === OrderStatus.INACTIVE && o.orderType === OrderType.STOP_LOSS && modifyingOrder?.batchId === o.batchId
        })
    }, [basketId, orderHistory, transactionType, modifyingOrder])

    const basketOpenTargetOrder = useMemo(() => {
        return (orderHistory || []).find(o => {
            return o.basketId === basketId && transactionType !== o.transactionType &&
                [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status) && o.orderType === OrderType.TARGET
        })
    }, [basketId, orderHistory, transactionType])

    const basketInactiveTargetOrder = useMemo(() => {
        return (orderHistory || []).find(o => {
            return o.basketId === basketId && transactionType !== o.transactionType &&
                o.status === OrderStatus.INACTIVE && o.orderType === OrderType.TARGET && modifyingOrder?.batchId === o.batchId
        })
    }, [basketId, orderHistory, transactionType, modifyingOrder])

    const existingStopLoss = useMemo(() => !!basketOpenSlOrder || (!!basketInactiveSlOrder && !!modifyingOrder), [basketOpenSlOrder, basketInactiveSlOrder, modifyingOrder]);
    const existingTarget = useMemo(() => !!basketOpenTargetOrder || (!!basketInactiveTargetOrder && !!modifyingOrder), [basketOpenTargetOrder, basketInactiveTargetOrder, modifyingOrder]);

    useEffect(() => {
        snapshotRef.current = securitySnapshot;
    }, [securitySnapshot])

    useEffect(() => {
        if (!!basketOpenSlOrder) {
            dispatch(setQuickOrderData({
                addStopLoss: true,
                stopLoss: (basketOpenSlOrder?.triggerPrice ?? 0) / (selectedSecurity?.priceMultiplier ?? 100),
                stopLossType: 'price'
            }))
        } else if (basketInactiveSlOrder && modifyingOrder) {
            dispatch(setQuickOrderData({
                addStopLoss: true,
                stopLoss: (basketInactiveSlOrder?.triggerPrice ?? 0) / (selectedSecurity?.priceMultiplier ?? 100),
                stopLossType: 'price'
            }))
        } else {
            dispatch(setQuickOrderData({
                addStopLoss: false,
                stopLossType: 'points',
                stopLoss: ''
            }))
        }
    }, [basketOpenSlOrder, selectedSecurity, basketInactiveSlOrder, modifyingOrder])

    useEffect(() => {
        if (!!basketOpenTargetOrder) {
            dispatch(setQuickOrderData({
                addTarget: true,
                target: (basketOpenTargetOrder?.price ?? 0) / (selectedSecurity?.priceMultiplier ?? 100),
                targetType: 'price'
            }))
        } else if (basketInactiveTargetOrder && modifyingOrder) {
            dispatch(setQuickOrderData({
                addTarget: true,
                target: (basketInactiveTargetOrder?.price ?? 0) / (selectedSecurity?.priceMultiplier ?? 100),
                targetType: 'price'
            }))
        } else {
            dispatch(setQuickOrderData({
                addTarget: false,
                targetType: 'points',
                target: ''
            }))
        }
    }, [basketOpenTargetOrder, basketInactiveTargetOrder, modifyingOrder, selectedSecurity])

    const stopLossPrice = useMemo(() => {
        if (!addStopLoss || !stopLoss || stopLossType === 'mtm') return;

        if (existingStopLoss) return stopLoss;
        const limitOrder = [Broker_OrderType.LIMIT_ORDER, Broker_OrderType.STOPLOSS_ORDER].includes(brokerOrderType);

        const quote = transactionType === TransactionType.BUY ? orderBook?.asks[0] : orderBook?.bids[0];
        const basePrice = limitOrder ? price : (quote?.price ?? price);
        const sL = stopLossType === 'points' ? stopLoss : ((price / (selectedSecurity?.priceMultiplier ?? 100)) * stopLoss / 100);
        if (transactionType === TransactionType.BUY) {
            return basePrice / (selectedSecurity?.priceMultiplier ?? 100) - sL
        } else {
            return basePrice / (selectedSecurity?.priceMultiplier ?? 100) + sL;
        }
    }, [addStopLoss, stopLoss, stopLossType, brokerOrderType, price, transactionType, orderBook, existingStopLoss])

    const targetPrice = useMemo(() => {
        if (!addTarget || !target || targetType === 'mtm') return;

        if (existingTarget) return target;
        const limitOrder = [Broker_OrderType.LIMIT_ORDER, Broker_OrderType.STOPLOSS_ORDER].includes(brokerOrderType);
        const quote = transactionType === TransactionType.BUY ? orderBook?.asks[0] : orderBook?.bids[0];
        const basePrice = limitOrder ? price : (quote?.price ?? price);

        const tg = targetType === 'points' ? target : ((price / (selectedSecurity?.priceMultiplier ?? 100)) * target / 100);
        if (transactionType === TransactionType.BUY) {
            return basePrice / (selectedSecurity?.priceMultiplier ?? 100) + tg
        } else {
            return basePrice / (selectedSecurity?.priceMultiplier ?? 100) - tg;
        }
    }, [addTarget, target, targetType, brokerOrderType, price, transactionType, orderBook, existingTarget])

    const isMarginSecurity = useMemo(() => [SecurityCategory_CategoryCode.FUTURES, SecurityCategory_CategoryCode.OPTIONS].includes(selectedSecurity?.categoryCode), [selectedSecurity]);

    const productItems = useMemo(() => {
        const items = [
            {
                key: Product.INTRADAY,
                label: "Intraday",
                disabled: mode === 'slOrTarget' 
            },
            {
                key: isMarginSecurity ? Product.MARGIN : Product.CNC,
                label: isMarginSecurity ? "MARGIN" : "CNC",
                disabled: mode === 'slOrTarget' || !showAllTradeOptions
            },
            {
                key: Product.MTF,
                label: "MTF",
                disabled: selectedSecurity?.categoryCode !== SecurityCategory_CategoryCode.STOCKS || mode === 'slOrTarget' || !showAllTradeOptions
            }
        ]

        return items;
    }, [isMarginSecurity, selectedSecurity, mode, showAllTradeOptions])
    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setpopUpOver((prev: any) => !prev);
    };

    const handleClose = () => {
        setpopUpOver(false);
    };

    const updateCurrentMarketPrice = useCallback(() => {
        dispatch(
            setQuickOrderData({
            order: {
                price: snapshotRef.current.lastTradedPrice ?? 0
            }
            })
        );
    }, [dispatch]);

    useEffect(()=>{
        if(!showAllTradeOptions){
            dispatch(setQuickOrderData({
            order: {
                product: Product.INTRADAY
            }
        }))
            onModeChange("regular")
            setAdvancedMode(false)
        }
    },[showAllTradeOptions])

    useEffect(() => {
        return () => {
            websocketActions.setWebsocketSubscribeList(dispatch, {
                orderBookSubscriptions: [
                    {
                        tag: 'quickTradeOrderBook',
                        securities: [],
                        topBidAskOnly: false
                    }
                ],
                securitySnapshotSubscriptions: [
                    {
                        tag: 'quickTradeSecuritySnapshot',
                        securities: []
                    }
                ]
            })
        }
    }, [])

    const handleRequiredMarginRefresh = useCallback(async () => {
        if (!primaryTradingAccount || primaryTradingAccount.status == TradingAccountStatus.BROKEN) return
        if (selectedSecurity?.instrument === Instrument[Instrument.IDX]) return
        const snapshot = snapshotRef.current;
        try {
            const order: MarginFromBrokerRequest_Order = {
                security: {
                    code: selectedSecurity?.code ?? '',
                    exchange: selectedSecurity?.exchange ?? 0
                },
                transactionType,
                orderType: brokerOrderType,
                position: Math.round(Number(quantity)),
                product,
                price: [Broker_OrderType.MARKET_ORDER, Broker_OrderType.STOPLOSS_MARKET_ORDER].includes(brokerOrderType) ? snapshot?.lastTradedPrice ?? 0 : Math.round(Number(price)),
                variety: Broker_Variety.REGULAR,
                ...([Broker_OrderType.STOPLOSS_MARKET_ORDER, Broker_OrderType.STOPLOSS_ORDER].includes(brokerOrderType)
                    ? { triggerPrice: Math.round(Number(triggerPrice)) }
                    : {}),
                exchange: selectedSecurity?.exchange ?? 0
            }
            const tradingAccount: MarginFromBrokerRequest_TradingAccount = {
                accountId: primaryTradingAccount.accountId,
                brokerId: primaryTradingAccount.brokerCode
            }
            const request: MarginFromBrokerRequest = {
                orders: [order],
                tradingAccount: tradingAccount
            }
            const requiredMargin = await getRequiredMarginFromBroker({ request })
            setRequiredMargin(requiredMargin)
            if (selectedSecurity.segment == Segment.CM && primaryTradingAccount.brokerCode == BrokerId.PAPER_TRADING) {
                if (brokerOrderType == Broker_OrderType.MARKET_ORDER || brokerOrderType == Broker_OrderType.STOPLOSS_MARKET_ORDER) {
                    setRequiredMargin({ totalMargin: ((snapshot?.lastTradedPrice ?? 0) * quantity / (selectedSecurity?.priceMultiplier ?? 100)), brokerCharges: requiredMargin.brokerCharges })
                } else {
                    setRequiredMargin({ totalMargin: price * quantity / (selectedSecurity?.priceMultiplier ?? 100), brokerCharges: requiredMargin.brokerCharges })
                }
                return
            }
        } catch (e) {
            console.error('Failed to refresh required margin', e)
        }
    }, [primaryTradingAccount, selectedSecurity, transactionType, brokerOrderType, quantity, product, price, triggerPrice])

    const debouncedRefresh = useMemo(
        () => debounce(300, handleRequiredMarginRefresh),
        [handleRequiredMarginRefresh]
    )

    useEffect(() => {
        if(showAllTradeOptions) {
            debouncedRefresh()
        }
        return () => debouncedRefresh.cancel()
    }, [debouncedRefresh, showAllTradeOptions])

    const handleAllMarginsRefresh = useCallback(async () => {
        if (!primaryTradingAccount || primaryTradingAccount.status !== TradingAccountStatus.ACTIVE) {
            if (primaryTradingAccount && primaryTradingAccount.brokerCode !== BrokerId.DHAN) {
                const fetchedUrl = await generateBrokerLoginUrl(primaryTradingAccount.brokerCode, keyValueArrayFromTradingAccountParams(primaryTradingAccount.tradingAccountParameters))
                window.open(fetchedUrl, '_self')
            }else{
               navigate(ROUTES.tradingAccount) 
            }
            return 
        }
        try {
            const body = {
                accountId: primaryTradingAccount.accountId,
                brokerCode: primaryTradingAccount.brokerCode,
                brokerAccountId: primaryTradingAccount.brokerAccountId,
                created: primaryTradingAccount.created
            }
            const response = await syncAccountMarginFromBroker({ body })
            dispatch(setMarginForAccount({ accountId: primaryTradingAccount.accountId, margin: response }))
        } catch (e) {
            console.error('Failed to refresh margin', e)
        }
    }, [primaryTradingAccount, handleRequiredMarginRefresh]);

    useEffect(() => {
        setClientOrderId(uuid());
        setSlClientOrderId(uuid());
        setTargetClientOrderId(uuid());
    }, [primaryTradingAccount, transactionType])

    useEffect(() => {
        if (selectedSecurity) {
            const key = getSecurityKey(selectedSecurity.exchange, selectedSecurity.code)
            if (key === selectedSecurityRef.current) return
            selectedSecurityRef.current = key;
            setClientOrderId(uuid());
            setSlClientOrderId(uuid());
            setTargetClientOrderId(uuid());

            // Fetch snapshots for all available exchanges
            const securitiesToFetch = availableExchanges.map(exchange => ({
                exchange,
                code: selectedSecurity.code
            }));

            (async () => {
                try {
                    const response = await fetchSecuritySnapshotsBulk({
                        securities: securitiesToFetch,
                        dispatch
                    })
                    const snapshot = response.securitySnapshots.find(s =>
                        s.security?.code === selectedSecurity?.code &&
                        s.security?.exchange === selectedSecurity?.exchange
                    )
                    // Only set price from snapshot if not modifying an existing order
                    if (snapshot && type !== 'modify' && renderedFirstTime.current) {
                        dispatch(setQuickOrderData({
                            order: {
                                price: snapshot?.lastTradedPrice ?? 0
                            }
                        }))
                    }
                    renderedFirstTime.current = true
                } catch (e) {
                    console.error('Error fetching snapshot:', e)
                }
            })()

            // Only set default quantity if not modifying an existing order
            if (type !== 'modify' && !quantity) {
                dispatch(setQuickOrderData({
                    order: {
                        quantity: selectedSecurity?.lotSize || 1
                    }
                }))
            }

            // Subscribe to all available exchanges
            websocketActions.setWebsocketSubscribeList(dispatch, {
                securitySnapshotSubscriptions: [
                    {
                        tag: 'quickTradeSecuritySnapshot',
                        securities: securitiesToFetch
                    }
                ]
            })
        }
    }, [selectedSecurity, availableExchanges, type])

    const onClose = useCallback(() => {
        dispatch(removeQuickOrderData())
    }, [])

    const isSecurityUnderSurveillance = useMemo(() => {
        return selectedSecurity?.surveillanceIndicator;
    }, [selectedSecurity]);

    const surveillanceInfo = useMemo(() => {
        const code = Number(selectedSecurity?.surveillanceIndicator);
        return {
            symbol: selectedSecurity?.code ?? '',
            warningTitle: "Regulatory Caution",
            cautions: code ? [String(code)] : [],
        };
    }, [selectedSecurity]);

    const onModeChange = useCallback((mode: QuickTradeData['mode']) => {
        const updates: Partial<QuickTradeData> = { mode };

        if (mode === "trigger") {
            updates.order = {
                orderType: OrderType.TRIGGER,
                brokerOrderType: Broker_OrderType.STOPLOSS_ORDER,
                triggerPrice: Math.round(price)
            }
        } else {
            updates.order = {
                orderType: OrderType.NORMAL,
                brokerOrderType: Broker_OrderType.LIMIT_ORDER,
                triggerPrice: 0
            }
        }
        dispatch(setQuickOrderData(updates))
    }, [price])


    const handlePlaceOrder = useCallback(async () => {
        if (!primaryTradingAccount || !tradingAccounts || tradingAccounts?.length === 0) {
            dispatch(openConnectAccountDialog({
                type: 'connect',
                tradingAccountId: ''
            }))
        } else if (primaryTradingAccount.status !== TradingAccountStatus.ACTIVE) {
            dispatch(openConnectAccountDialog({
                type: 'reconnect',
                tradingAccountId: primaryTradingAccount.accountId
            }))
        }
        else if (!confirmedFromSurveillance && isSecurityUnderSurveillance) {
            setShowSurveillanceDialog(true);
            return;
        }
        else {
            const requestOrders: Order[] = [
                {
                    security: {
                        code: selectedSecurity?.code ?? '',
                        exchange: selectedSecurity?.exchange ?? 0
                    },
                    transactionType,
                    orderType,
                    brokerOrderType,
                    quantity: quantity,
                    isBasket: false,
                    batchId: modifyingOrder ? modifyingOrder.batchId : batchId,
                    orderDate: Date.now(),
                    product,
                    price: brokerOrderType === Broker_OrderType.MARKET_ORDER || brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER ? 0 : Math.round(Number(price)),
                    triggerPrice: [OrderType.TRIGGER, OrderType.STOP_LOSS].includes(orderType) ? Math.round(Number(triggerPrice)) : 0,
                    brokerVariety: Broker_Variety.REGULAR,
                    validity,
                    clientOrderId: clientOrderId,
                    disclosedQuantity: disclosedQuantity > 0 ? disclosedQuantity : undefined,
                    orderId,
                    immediateExecutePaperOrder: !showAllTradeOptions,
                }
            ];

            if (showAllTradeOptions && addStopLoss && stopLossPrice) {
                requestOrders.push({
                    security: {
                        code: selectedSecurity?.code ?? '',
                        exchange: selectedSecurity?.exchange ?? 0
                    },
                    transactionType: transactionType === TransactionType.BUY ? TransactionType.SELL : TransactionType.BUY,
                    orderType: OrderType.STOP_LOSS,
                    brokerOrderType: Broker_OrderType.STOPLOSS_MARKET_ORDER,
                    quantity: Number(quantity),
                    isBasket: false,
                    batchId: modifyingOrder ? modifyingOrder.batchId : batchId,
                    orderDate: Date.now(),
                    product,
                    price: 0,
                    triggerPrice: getRoundedPrice(stopLossPrice * (selectedSecurity?.priceMultiplier ?? 100), selectedSecurity?.tickSize ?? 5),
                    brokerVariety: Broker_Variety.REGULAR,
                    validity,
                    clientOrderId: orderId && basketInactiveSlOrder ? basketInactiveSlOrder.clientOrderId : slClientOrderId,
                    orderExecutionParams: {},
                    orderId: orderId && basketInactiveSlOrder ? basketInactiveSlOrder.orderId : undefined,
                    immediateExecutePaperOrder: false,
                    marketProtection: -1
                })
            }

            if (showAllTradeOptions && addTarget && targetPrice) {
                requestOrders.push({
                    security: {
                        code: selectedSecurity?.code ?? '',
                        exchange: selectedSecurity?.exchange ?? 0
                    },
                    transactionType: transactionType === TransactionType.BUY ? TransactionType.SELL : TransactionType.BUY,
                    orderType: OrderType.TARGET,
                    brokerOrderType: Broker_OrderType.LIMIT_ORDER,
                    quantity: Number(quantity),
                    isBasket: false,
                    batchId: modifyingOrder ? modifyingOrder.batchId : batchId,
                    orderDate: Date.now(),
                    product,
                    price: getRoundedPrice(targetPrice * (selectedSecurity?.priceMultiplier ?? 100), selectedSecurity?.tickSize ?? 5),
                    triggerPrice: 0,
                    brokerVariety: Broker_Variety.REGULAR,
                    validity,
                    clientOrderId: orderId && basketInactiveTargetOrder ? basketInactiveTargetOrder.clientOrderId : targetClientOrderId,
                    orderExecutionParams: {},
                    orderId: orderId && basketInactiveTargetOrder ? basketInactiveTargetOrder.orderId : undefined,
                    immediateExecutePaperOrder: false
                })
            }

            const request: PlaceOrModifyOrdersRequest = {
                tradingAccountId: primaryTradingAccount.accountId ?? '',
                order: requestOrders,
                buyBeforeSell: false
            }
            try {
                setRequestInProgress(true);
                const response = await placeOrModifyOrder(request);
                const updates: Order[] = [];
                let successCount = 0;
                (response.orderResponse || []).forEach((orderResponse, i) => {
                    const { successful, commitOrder, brokerOrderId, orderId, errorMessage, order } = orderResponse;
                    if (!successful) {
                        addOrderAlertMessage({
                            ...request.order[i],
                            status: OrderStatus.FAILED,
                            message: errorMessage || 'Unable to place order'
                        })
                        setClientOrderId(uuid());
                        setSlClientOrderId(uuid());
                        setTargetClientOrderId(uuid());
                        setBatchId(uuid());
                    } else {
                        successCount++;
                        if (order) {
                            updates.push(order)
                            addOrderAlertMessage({
                                ...order,
                                message: 'Order placed successfully'
                            })
                        }
                    }
                })
                updates.length && dispatch(updateOrderHistory({ updates }))
                successCount === requestOrders.length && onClose();
                setRequestInProgress(false);
            } catch (error: any) {
                addOrderAlertMessage({
                    ...request.order[0],
                    status: OrderStatus.FAILED,
                    message: error.message || 'Unable to place order'
                })
                setRequestInProgress(false);
            }
        }
    }, [primaryTradingAccount, tradingAccounts, dispatch, selectedSecurity, transactionType, orderType, quantity, product, price,
        triggerPrice, onClose, clientOrderId, confirmedFromSurveillance, type, orderId, mode, disclosedQuantity, marketProtection,
        addStopLoss, stopLossPrice, slClientOrderId, addTarget, targetPrice, targetClientOrderId, existingStopLoss, existingTarget,
        brokerOrderType, basketInactiveSlOrder, modifyingOrder, showAllTradeOptions
    ])

    const handleQuantityChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        AnalyticsManager.logEvent('Quantity Changed', { source: 'quick-trade-window' })
        let value = Number(e.target.value)
        if (value >= (selectedSecurity?.volumeFreezeQuantity ?? value)) {
            value = (selectedSecurity?.volumeFreezeQuantity ?? value) - 1
        }

        if (maxQuantity && value >= maxQuantity) value = maxQuantity
        if (value < 0) {
            return
        }
        dispatch(setQuickOrderData({
            order: {
                quantity: value
            }
        }))
        // setQuantity(e.target.value ? value.toString() : '')
    }, [maxQuantity, selectedSecurity])

    const handleQuantityBlur = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const multiple = selectedSecurity?.lotSize ?? 1
        const value = Number(e.target.value) > 0 ? Number(e.target.value) : selectedSecurity?.lotSize ?? 1
        let roundedValue = Math.round(Math.round(value / multiple) * multiple)
        if (roundedValue >= (selectedSecurity?.volumeFreezeQuantity ?? roundedValue)) {
            roundedValue = (selectedSecurity?.volumeFreezeQuantity ?? roundedValue) - 1
        }
        if (roundedValue <= 0) {
            roundedValue = selectedSecurity?.lotSize ?? 1
        }
        if (maxQuantity && roundedValue >= maxQuantity) roundedValue = maxQuantity
        dispatch(setQuickOrderData({
            order: {
                quantity: roundedValue
            }
        }))
        // setQuantity(roundedValue.toString())
    }, [maxQuantity, selectedSecurity])

    const handlePriceChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = Number(e.target.value)
        if (value < 0) return;
        dispatch(setQuickOrderData({
            order: {
                price: Math.round(value * (selectedSecurity?.priceMultiplier ?? 100))
            }
        }))
    }, [selectedSecurity])


    const handleTriggerPriceChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,) => {
        const value = Number(e.target.value)
        if (value < 0) return;
        dispatch(setQuickOrderData({
            order: {
                triggerPrice: Math.round(value * (selectedSecurity?.priceMultiplier ?? 100))
            }
        }))
    }, [selectedSecurity])

    const handlePriceBlur = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = Number(e.target.value) > 0 ? Number(e.target.value) : 1
        const multiple = selectedSecurity?.tickSize ?? 5
        const roundedValue = (Math.round(Number(Math.round(value * 100)) / multiple) * multiple) / 100
        dispatch(setQuickOrderData({
            order: {
                price: Math.round(roundedValue * (selectedSecurity?.priceMultiplier ?? 100))
            }
        }))
    }, [selectedSecurity])

    const handleTriggerPriceBlur = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = Number(e.target.value) > 0 ? Number(e.target.value) : 1
        const multiple = selectedSecurity?.tickSize ?? 5
        const roundedValue = (Math.round(Number(Math.round(value * 100)) / multiple) * multiple) / 100
        dispatch(setQuickOrderData({
            order: {
                triggerPrice: Math.round(roundedValue * (selectedSecurity?.priceMultiplier ?? 100))
            }
        }))
    }, [selectedSecurity])



    const handleTransactionTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.checked
        dispatch(setQuickOrderData({
            order: {
                transactionType: value ? TransactionType.BUY : TransactionType.SELL
            }
        }))
        // setTransactionType(value ? TransactionType.BUY : TransactionType.SELL)
        AnalyticsManager.logEvent('Transaction Type Changed', {
            source: 'quick-trade-window',
            transactionType: transactionTypeToJSON(value ? TransactionType.BUY : TransactionType.SELL)
        })
    }, [])

    const { containerRef, handleRef, position } = useDraggable<HTMLDivElement, HTMLDivElement>(document.body)

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation()
                onClose?.()
            }
        }

        if (openDialog) {
            window.addEventListener('keydown', handleKeyDown)
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [openDialog, onClose])

    useEffect(() => {
        if (confirmedFromSurveillance) {
            handlePlaceOrder();
        }
    }, [confirmedFromSurveillance]);

    const disableEditableButtons = useMemo(() => Boolean(isSquareOffOrder || type === 'modify'), [isSquareOffOrder, type])

    const onProductChange = useCallback((value: Product) => {
        dispatch(setQuickOrderData({
            order: {
                product: value
            }
        }))

    }, [])

    const onOrderTypeToggle = useCallback((e: any, value: Broker_OrderType) => {
        setAnchorEl(null)
        e.stopPropagation();
        let updates: Partial<QuickTradeData> = {
            order: {}
        };

        if ((value === Broker_OrderType.LIMIT_ORDER)) {
            const quote = transactionType === TransactionType.BUY ? orderBook?.asks[0] : orderBook?.bids[0];
            if (quote) {
                updates.order.price = quote.price;
            }
            if (mode === "trigger") {
                updates.order.brokerOrderType = Broker_OrderType.STOPLOSS_ORDER
            } else {
                updates.order.brokerOrderType = Broker_OrderType.LIMIT_ORDER
            }
        } else {
            if (mode === "trigger") {
                updates.order.brokerOrderType = Broker_OrderType.STOPLOSS_MARKET_ORDER
            } else {
                updates.order.brokerOrderType = Broker_OrderType.MARKET_ORDER
            }
        }
        dispatch(setQuickOrderData(updates))
    }, [selectedSecurity, mode, orderType, transactionType, orderBook, dispatch])

    const onValidityChange = useCallback((e: any, value: Validity) => {
        dispatch(setQuickOrderData({
            order: {
                validity: value
            }
        }))
    }, [selectedSecurity, mode])

    const toggleAdvanced = useCallback(() => {
        setAdvancedMode(!advancedMode);
    }, [advancedMode])

    const onDisclosedQuantityChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const value = Number(e.target.value);
        if (value < 0) return;
        dispatch(setQuickOrderData({
            order: {
                disclosedQuantity: value
            }
        }))
    }, [])

    const toggleAddStopLoss = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        if(!showAllTradeOptions) return;
        dispatch(setQuickOrderData({
            addStopLoss: e.target.checked
        }));
        setTimeout(()=>{
            stopLossInputRef.current?.focus()
        }, 100)
    }, [showAllTradeOptions])

    const onStopLossChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const value = Number(e.target.value);
        if (e.target.value === '') {
            dispatch(setQuickOrderData({
                stopLoss: ''
            }))
            return;
        }

        if (value >= 0) {
            dispatch(setQuickOrderData({
                stopLoss: value
            }))
        }
    }, [])

    const onStopLossTypeChange = useCallback((e: any, value: string) => {
        stopLossInputRef.current?.focus()
        dispatch(setQuickOrderData({
            stopLossType: value as SlTargetType
        }))
    }, [])

    const toggleAddTarget = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        if(!showAllTradeOptions) return;
        dispatch(setQuickOrderData({
            addTarget: e.target.checked
        }));
        setTimeout(()=>{
            targetInputRef.current?.focus()
        }, 100)
    }, [showAllTradeOptions])

    const onTargetChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const value = Number(e.target.value);
        if (e.target.value === '') {
            dispatch(setQuickOrderData({
                target: ''
            }))
            return;
        }
        if (value >= 0) {
            dispatch(setQuickOrderData({
                target: value
            }))
        }
    }, [selectedSecurity])

    const onTargetTypeChange = useCallback((e: any, value: string) => {
        targetInputRef.current?.focus()
        dispatch(setQuickOrderData({
            targetType: value as SlTargetType
        }))
    }, [])


    const stopLossField = useMemo(() => {
        return <>
            <Stack direction="row" alignItems="center" sx={{ marginBottom: 0.5, justifyContent: 'space-between' }} >
                <Stack direction="row" alignItems="center" >
                    <Checkbox disabled={!showAllTradeOptions} checked={addStopLoss == undefined ? false : addStopLoss} onChange={toggleAddStopLoss} size='small' sx={{ padding: 0, marginRight: 0.5 }} />
                    <Typography variant='body1' className={classes.slTargetLabel}>STOP LOSS</Typography>
                </Stack>
            </Stack>
            <TextField
                InputLabelProps={{
                    shrink: true
                }}
                type="number"
                size="small"
                fullWidth
                value={stopLoss}
                disabled={!addStopLoss || !showAllTradeOptions}
                autoComplete='false'
                onChange={onStopLossChange}
                inputRef={stopLossInputRef}
                inputProps={{
                    step: (selectedSecurity?.tickSize ?? 100) / (selectedSecurity?.priceMultiplier ?? 100)
                }}
                InputProps={{
                    sx: {
                        '& input[type=number]': {
                            MozAppearance: 'textfield'
                        },
                        paddingRight: 0.5,
                    },
                    endAdornment: existingStopLoss ? null : (
                        <>
                            {!addStopLoss && <div className={classes.disabledInputClickBackdrop} onClick={(e) => {
                                const newEvent = e as any
                                newEvent.target = { checked: true }
                                toggleAddStopLoss(newEvent)
                            }} />}
                            <ToggleButtonGroup
                                size="small"
                                value={stopLossType}
                                aria-label="text alignment"
                                disabled={!showAllTradeOptions}
                                sx={{
                                    height: "28px",
                                    '.MuiButtonBase-root': {
                                        borderColor: (theme) => theme.palette.background.default
                                    },
                                }}
                            >
                                <ToggleButton disabled={!showAllTradeOptions} aria-label="left aligned" selected={!showAllTradeOptions? false :stopLossType === "points"} value="points" onClick={onStopLossTypeChange}>
                                    Pts
                                </ToggleButton>
                                <ToggleButton disabled={!showAllTradeOptions} aria-label="left aligned" selected={!showAllTradeOptions? false :stopLossType === "percentage"} value="percentage" onClick={onStopLossTypeChange}>
                                    %
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </>
                    )
                }}
            />
            {stopLossPrice && <FormHelperText>SL Price: {stopLossPrice?.toFixed(2)}</FormHelperText>}
        </>
    }, [selectedSecurity, addStopLoss, stopLoss, showAllTradeOptions, toggleAddStopLoss, onStopLossChange, stopLossType, onStopLossTypeChange, stopLossPrice, existingStopLoss])

    const onPriceInputClick = useCallback((e: any) => {

        [Broker_OrderType.STOPLOSS_MARKET_ORDER, Broker_OrderType.MARKET_ORDER].includes(brokerOrderType) && onOrderTypeToggle(e, Broker_OrderType.LIMIT_ORDER)
    }, [brokerOrderType])

    const targetField = useMemo(() => {
        return <>
            <Stack direction="row" alignItems="center" sx={{ marginBottom: 0.5, justifyContent: 'space-between' }} >
                <Stack direction="row" alignItems="center" >
                    <Checkbox disabled={!showAllTradeOptions} checked={addTarget === undefined ? false : addTarget} onChange={toggleAddTarget} size='small' sx={{ padding: 0, marginRight: 0.5 }} />
                    <Typography variant='body1' className={classes.slTargetLabel}>TARGET</Typography>
                </Stack>
            </Stack>
            <TextField
                InputLabelProps={{
                    shrink: true
                }}
                type="number"
                size="small"
                fullWidth
                value={target}
                disabled={!addTarget || !showAllTradeOptions}
                autoComplete='false'
                onChange={onTargetChange}
                inputRef={targetInputRef}
                inputProps={{
                    step: (selectedSecurity?.tickSize ?? 100) / (selectedSecurity?.priceMultiplier ?? 100)
                }}
                InputProps={{
                    sx: {
                        '& input[type=number]': {
                            MozAppearance: 'textfield'
                        },
                        paddingRight: 0.5,
                    },
                    endAdornment: existingTarget ? null : (
                        <>
                            {!addTarget && <div className={classes.disabledInputClickBackdrop} onClick={(e) => {
                                const newEvent = e as any
                                newEvent.target = { checked: true }
                                toggleAddTarget(newEvent)
                            }} />}
                            <ToggleButtonGroup
                                size="small"
                                value={targetType}
                                aria-label="text alignment"
                                disabled={!showAllTradeOptions}
                                sx={{
                                    height: "28px",
                                    '.MuiButtonBase-root': {
                                        borderColor: (theme) => theme.palette.background.default
                                    },
                                }}
                            >
                                <ToggleButton disabled={!showAllTradeOptions} aria-label="left aligned" selected={!showAllTradeOptions ? false : targetType === "points"} value="points" onClick={onTargetTypeChange}>
                                    Pts
                                </ToggleButton>
                                <ToggleButton disabled={!showAllTradeOptions} aria-label="left aligned" selected={!showAllTradeOptions ? false : targetType === "percentage"} value="percentage" onClick={onTargetTypeChange}>
                                    %
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </>
                    )
                }}
            />
            {targetPrice && <FormHelperText>Tgt Price: {targetPrice.toFixed(2)}</FormHelperText>}
        </>
    }, [selectedSecurity, addTarget, target, showAllTradeOptions, toggleAddTarget, onTargetChange, targetType, onTargetTypeChange, targetPrice, existingTarget])

    const handleExchangeChange = useCallback((exchange: Exchange) => {
        dispatch(setQuickOrderData({
            order: {
                security: {
                    exchange,
                    code: order.security?.code
                }
            }
        }))
        AnalyticsManager.logEvent('Exchange Changed', {
            source: 'quick-trade-window',
            exchange: exchange === Exchange.NSE ? 'NSE' : 'BSE'
        })
    }, [order, availableExchanges, dispatch])

    const exchangeSelector = useMemo(() => {
        if (availableExchanges.length <= 1) return null;

        // In modify mode, show only the selected exchange without radio button
        if (type === 'modify') {
            const securityKey = getSecurityKey(selectedSecurity.exchange, selectedSecurity.code);
            const snapshot = snapshots[securityKey]
            const price = snapshot ? ((snapshot.lastTradedPrice ?? 0) / (selectedSecurity?.priceMultiplier ?? 100)).toFixed(2) : '0.00'

            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <Typography
                        fontSize="13px"
                        color="white"
                        sx={{ marginRight: '4px' }}
                    >
                        {exchangeToJSON(selectedSecurity.exchange)}
                    </Typography>
                    <Typography
                        fontSize="13px"
                        color="white"
                        className="font-feature-num"
                    >
                        ₹{price}
                    </Typography>
                </Box>
            )
        }

        return <Stack direction="row" spacing={1}>
            {availableExchanges.map((exchange) => {
                const securityKey = getSecurityKey(exchange, selectedSecurity.code);
                const snapshot = snapshots[securityKey]
                const isSelected = exchange === selectedSecurity.exchange
                const price = snapshot ? ((snapshot.lastTradedPrice ?? 0) / (selectedSecurity?.priceMultiplier ?? 100)).toFixed(2) : '0.00'

                return (
                    <Box
                        key={exchange}
                        onClick={() => handleExchangeChange(exchange)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            opacity: isSelected ? 1.0 : 0.6,
                            transition: 'opacity 0.2s ease',
                            '&:hover': {
                                opacity: 1.0
                            }
                        }}
                    >
                        <Radio
                            checked={isSelected}
                            size="small"
                            sx={{
                                color: 'white',
                                '&.Mui-checked': {
                                    color: 'white'
                                },
                                transform: 'scale(0.6)',
                                padding: 0,
                                marginRight: '2px'
                            }}
                        />
                        <Typography
                            fontSize="13px"
                            color="white"
                            sx={{ marginRight: '4px' }}
                        >
                            {exchangeToJSON(exchange)}
                        </Typography>
                        <Typography
                            fontSize="13px"
                            color="white"
                            className="font-feature-num"
                        >
                            ₹{price}
                        </Typography>
                    </Box>
                )
            })}
        </Stack>
    }, [availableExchanges, selectedSecurity, snapshots, type, handleExchangeChange])

    return (
        <Fade appear={false} in={openDialog}>
            <Paper
                elevation={3}
                sx={{
                    width: {
                        sm: '90%',
                        lg: 600
                    },
                    overflow: 'hidden',
                    right: position?.right ?? 60,
                    bottom: position?.bottom ?? 5
                }}
                tabIndex={-1}
                className={classes.container}
                ref={containerRef}
            >
                <DialogTitle onPointerDown={() => setIsDragging(true)}
                    onPointerUp={() => setIsDragging(false)}
                    onPointerLeave={() => setIsDragging(false)} className={cx('quick-trade-drage-handle', classes.header)}
                    sx={{
                        cursor: isDragging ? 'grabbing' : 'grab',
                        padding: '10px 15px',
                        userSelect: 'none',
                        background: transactionType === TransactionType.BUY ? blue : red,
                    }}
                    ref={handleRef}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Stack direction={'row'} alignItems={'center'} spacing={0.5}
                            className={classes.headerText}>
                            {selectedSecurity?.logoUrl ?
                                <Avatar alt={selectedSecurity?.displayName ?? ''}
                                    src={`${LOGOS_BASE_URL}${selectedSecurity?.logoUrl}.svg`}
                                    sx={{ width: 24, height: 24 }} />
                                :
                                <Avatar alt={selectedSecurity?.displayName ?? ''}
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        fontSize: 12,
                                        color: 'white',
                                        bgcolor: getColorForSymbol(selectedSecurity?.symbol || selectedSecurity?.code || '', theme.palette.background.default)
                                    }}>{(selectedSecurity?.symbol || selectedSecurity?.code || '').split(' ').map((word) => word[0]?.toUpperCase()).slice(0, 2).join('')}</Avatar>
                            }
                            <Box sx={{ textWrap: 'nowrap' }}>
                                <Typography component={'span'} fontWeight={'500'}
                                    fontSize={'16px'}>{selectedSecurity?.symbol}</Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {isSecurityUnderSurveillance ? <IconButton onClick={handleOpen} size="small" ref={anchorRef} sx={{
                                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                borderRadius: 1,
                                transition: 'background-color 0.2s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 193, 7, 0.2)',
                                },
                                '&:active': {
                                    backgroundColor: 'rgba(255, 193, 7, 0.3)',
                                },
                            }} >
                                <AlertIcon />
                            </IconButton> : null}

                            <PopUpPopover open={popUpOver} onClose={handleClose} info={surveillanceInfo} anchorEl={anchorRef.current} />
                            <FormControlLabel
                                control={
                                    <ToggleSwitch
                                        offColor="#fff"
                                        onColor="#fff"
                                        backgroundOpacity={0.5}
                                        thumbColor="#fff"
                                        checked={transactionType === TransactionType.BUY}
                                        disabled={disableEditableButtons}
                                        onChange={disableEditableButtons ? undefined : handleTransactionTypeChange}
                                        sx={{ marginLeft: '5px' }}
                                    />}
                                label={transactionType === TransactionType.SELL ? 'Sell' : 'Buy'}
                                labelPlacement="start"
                                style={{ marginRight: '6px', cursor: disableEditableButtons ? 'default' : 'pointer', opacity: disableEditableButtons ? 0.6 : 1 }}
                                className={classes.headerText}
                                disabled={false}
                            />
                        </Stack>
                    </Stack>

                    {exchangeSelector}
                </DialogTitle>
                <Divider />
                {mode !== "slOrTarget"  && showAllTradeOptions && <QuickTradeProductTabs disabled={type === "modify" || !showAllTradeOptions} onChange={onModeChange} />}
                <DialogContent style={{ padding: '15px' }}>
                    <Grid container rowGap={2} columnSpacing={1}>
                        {showAllTradeOptions && <Grid container item xs={12} marginBottom={2}>
                            <Grid item xs={6} sx={{ opacity: type === 'modify' ? 0.5 : 1 }}>
                                <GenericFilterTabs items={productItems} value={product} onChange={type === 'modify' ? undefined : onProductChange} disabled={type === 'modify' || !showAllTradeOptions} />
                            </Grid>
                        </Grid>}
                        <Grid item xs={6}>
                            <TextField
                                label="Qty"
                                InputLabelProps={{
                                    shrink: true
                                }}
                                type="number"
                                size="small"
                                fullWidth
                                value={Boolean(quantity) ? quantity : ''}
                                onChange={handleQuantityChange}
                                onBlur={handleQuantityBlur}
                                inputProps={{
                                    step: selectedSecurity?.lotSize ?? 1
                                }}
                                helperText={selectedSecurity?.categoryCode === SecurityCategory_CategoryCode.STOCKS ? '' : `Lot size: ${selectedSecurity?.lotSize ?? 1}`}
                                FormHelperTextProps={{
                                    sx: {
                                        marginTop: '2px',
                                        marginLeft: '2px'
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            {showAllTradeOptions && <OrderBookSelectionDialog
                                anchorEl={anchorEl?.element}
                                security={selectedSecurity}
                                handleOrderBookPriceChange={(price) => {
                                    const event = { target: { value: price.toString() } }
                                    if (anchorEl?.type === 'price') {
                                        handlePriceBlur(event as any)
                                    } else if (anchorEl?.type === 'triggerPrice') {
                                        handleTriggerPriceBlur(event as any)
                                    }
                                    setAnchorEl(null)
                                }}
                            />}
                            <ClickAwayListener onClickAway={()=>{
                                if(anchorEl?.type==='price') setAnchorEl(null)
                            }}>
                                <TextField
                                    label={brokerOrderType === Broker_OrderType.MARKET_ORDER ? 'Market price' : 'Price'}
                                    disabled={brokerOrderType === Broker_OrderType.MARKET_ORDER || brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    fullWidth
                                    type="number"
                                    size="small"
                                    value={brokerOrderType === Broker_OrderType.MARKET_ORDER || brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER ? 0 : Boolean(price) ? (price) / (selectedSecurity?.priceMultiplier ?? 100) : ''}
                                    onChange={handlePriceChange}
                                    onBlur={handlePriceBlur}
                                    onFocus={e => {
                                        setAnchorEl({
                                            element: e.currentTarget,
                                            type: 'price'
                                        })
                                    }}
                                    inputProps={{
                                        step: (selectedSecurity?.tickSize ?? 100) / (selectedSecurity?.priceMultiplier ?? 100)
                                    }}
                                    InputProps={{
                                        sx: {
                                            paddingRight: 0.5,
                                            '& input[type=number]': {
                                                MozAppearance: 'textfield'
                                            }
                                        },
                                        endAdornment: (<>
                                            {[Broker_OrderType.STOPLOSS_MARKET_ORDER, Broker_OrderType.MARKET_ORDER].includes(brokerOrderType) && <div className={classes.disabledInputClickBackdrop} onClick={onPriceInputClick} />}
                                            {showAllTradeOptions && <ToggleButtonGroup
                                                size="small"
                                                value={brokerOrderType}
                                                aria-label="text alignment"
                                                sx={{
                                                    height: "28px",
                                                    '.MuiButtonBase-root': {
                                                        borderColor: (theme) => theme.palette.background.default
                                                    },
                                                    position: 'relative',
                                                    zIndex: 2
                                                }}
                                                disabled={!showAllTradeOptions}
                                            >
                                                <ToggleButton aria-label="left aligned" selected={[Broker_OrderType.MARKET_ORDER, Broker_OrderType.STOPLOSS_MARKET_ORDER].includes(brokerOrderType)} value={Broker_OrderType.MARKET_ORDER} onClick={onOrderTypeToggle}>
                                                    MKT
                                                </ToggleButton>
                                                <ToggleButton aria-label="left aligned" selected={[Broker_OrderType.LIMIT_ORDER, Broker_OrderType.STOPLOSS_ORDER].includes(brokerOrderType)} value={Broker_OrderType.LIMIT_ORDER} onClick={onOrderTypeToggle}>
                                                    LMT
                                                </ToggleButton>
                                            </ToggleButtonGroup>}
                                            {!showAllTradeOptions &&
                                                <IconButton sx={{ p: 0, m: 0 }} onClick={updateCurrentMarketPrice}>
                                                    <RefreshIcon sx={{ width: 20, height: 20, stroke: blue }} />
                                                </IconButton>
                                            }
                                            
                                        </>
                                        )
                                    }}
                                />
                            </ClickAwayListener>
                        </Grid>
                        {(mode === 'trigger' || orderType === OrderType.STOP_LOSS) && <Grid container item xs={12} columnSpacing={1}>
                            <Grid item xs={6}>
                                <ClickAwayListener onClickAway={()=>{
                                    if(anchorEl?.type==='triggerPrice') setAnchorEl(null)
                                }}>
                                    <TextField
                                        label={'Trigger price'}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                        type="number"
                                        size="small"
                                        value={Boolean(triggerPrice) ? triggerPrice / (selectedSecurity?.priceMultiplier ?? 100) : ''}
                                        onChange={handleTriggerPriceChange}
                                        onBlur={handleTriggerPriceBlur}
                                        onFocus={e => {
                                            setAnchorEl({
                                                element: e.currentTarget,
                                                type: 'triggerPrice'
                                            })
                                        }}
                                        inputProps={{
                                            step: (selectedSecurity?.tickSize ?? 100) / (selectedSecurity?.priceMultiplier ?? 100)
                                        }}
                                        InputProps={{
                                            sx: {
                                                '& input[type=number]': {
                                                    MozAppearance: 'textfield'
                                                }
                                            }
                                        }}
                                    />
                                </ClickAwayListener>
                            </Grid>
                        </Grid>}
                    </Grid>
                </DialogContent>
                {mode !== 'slOrTarget'  && showAllTradeOptions && <> <Divider />
                    <DialogContent style={{ padding: '15px' }}>
                        {
                            mode !== 'trigger' ?
                            <Grid container columnSpacing={1} marginBottom={2}>
                                <Grid item xs={6} paddingLeft={0} >
                                    {stopLossField}
                                </Grid>
                                <Grid item xs={6} paddingLeft={0} >
                                    {targetField}
                                </Grid>
                            </Grid> : null
                        }
                        {showAllTradeOptions && <Grid container columnSpacing={1} rowGap={1}>
                            <Grid item xs={12} textAlign="center" marginBottom={advancedMode ? 1 : 0}>
                                <span role="button" className={classes.advancedButton} onClick={toggleAdvanced} >Advanced <ThinArrowDownIcon className={advancedMode ? classes.advancedActive : null} /></span>
                            </Grid>

                            {advancedMode && showAllTradeOptions && (
                                <Grid container item xs={12} rowGap={2} columnSpacing={1}>
                                    <Grid item xs={6} >
                                        <ToggleButtonGroup
                                            size="small"
                                            value={validity}
                                            aria-label="text alignment"
                                            sx={{
                                                height: "37px",
                                                '.MuiButtonBase-root': {
                                                    borderColor: (theme) => theme.palette.background.default
                                                },
                                            }}
                                        >
                                            <ToggleButton aria-label="left aligned" selected={validity === Validity.DAY} value={Validity.DAY} onClick={onValidityChange}>
                                                DAY
                                            </ToggleButton>
                                            <ToggleButton aria-label="left aligned" selected={validity === Validity.IOC} value={Validity.IOC} onClick={onValidityChange}>
                                                IOC
                                            </ToggleButton>
                                            <ToggleButton aria-label="left aligned" selected={validity === Validity.GTT} value={Validity.GTT} onClick={onValidityChange}>
                                                GTT
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label={'Disclosed Qty'}
                                            InputLabelProps={{
                                                shrink: true
                                            }}
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={disclosedQuantity}
                                            disabled={isMarginSecurity}
                                            onChange={onDisclosedQuantityChange}
                                            // onBlur={(e) => handlePriceBlur(e, true)}
                                            inputProps={{
                                                step: selectedSecurity?.lotSize ?? 1
                                            }}
                                            InputProps={{
                                                sx: {
                                                    '& input[type=number]': {
                                                        MozAppearance: 'textfield'
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label={'Market Protection (%)'}
                                            InputLabelProps={{
                                                shrink: true
                                            }}
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={marketProtection}
                                            disabled={[Broker_OrderType.LIMIT_ORDER, Broker_OrderType.STOPLOSS_ORDER].includes(brokerOrderType)}
                                            InputProps={{
                                                sx: {
                                                    '& input[type=number]': {
                                                        MozAppearance: 'textfield'
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>}
                    </DialogContent>
                </>}
                {!showAllTradeOptions &&
                <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{
                        px: "15px",
                        py: 0.5,
                        borderRadius: 1,
                        mt: 1,
                    }}
                    >
                    <InfoIcon sx={{ fontSize: '1rem', color: 'darkOrange' }} />
                    <Typography fontSize={12} color="darkOrange">
                        Practice mode is limited. Connect a broker to unlock all features.
                    </Typography>
                </Stack>
                }
                <Divider />
                <DialogActions style={{ padding: '10px' }}>
                    <Stack direction={'row'} justifyContent={'space-between'} width={'100%'}>
                        <Stack direction={'row'} gap={2}>
                            <TradingAccountSelector disabled={disableEditableButtons} defaultTradingAccountId={tradingAccountId} closeQuickTradeWindow={onClose} />
                            {(() => {
                                if(!showAllTradeOptions) return null
                                const isAccountActive = primaryTradingAccount?.status === TradingAccountStatus.ACTIVE
                                const hasRequiredMargin = isAccountActive && requiredMargin !== undefined
                                const hasAvailableMargin = isAccountActive && primaryTradingAccountMarginAvailable !== undefined
                                return (
                                    <Stack direction={"row"} gap={2} alignItems={'center'} justifyContent={'center'}>
                                        <Stack direction={"row"} gap={1} sx={{ pr: !isAccountActive ? 3 : 0 }} alignItems={'center'}>
                                            <Typography fontWeight={'medium'} fontSize={12} color="text.secondary">
                                                Required
                                            </Typography>
                                            {hasRequiredMargin ? (
                                                <Typography fontWeight={'medium'} fontSize={12} color={blue}>
                                                    {`₹${requiredMargin.totalMargin.toLocaleString('en-IN')}`}
                                                </Typography>
                                            ) : (
                                                <Tooltip title={`${primaryTradingAccount ? 'Reconnect trading account' : 'Connect a trading account'} to view required margin`} arrow placement="top">
                                                    <IconButton
                                                        sx={{
                                                            p: 0,
                                                            m: 0,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={handleAllMarginsRefresh}
                                                    >
                                                        <RefreshIcon sx={{ width: 16, height: 16, stroke: blue }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {hasRequiredMargin && requiredMargin.brokerCharges != null && (
                                                <Typography fontWeight={'medium'} fontSize={12} color="text.secondary">
                                                    {`+${requiredMargin.brokerCharges.toLocaleString('en-IN')}`}
                                                </Typography>
                                            )}
                                        </Stack>
                                        <Stack direction={"row"} gap={1} alignItems={'center'}>
                                            <Typography fontWeight={'medium'} fontSize={12} color="text.secondary">
                                                Available
                                            </Typography>
                                            {hasAvailableMargin ? (
                                                <Typography fontWeight={'medium'} fontSize={12} color={blue}>
                                                    {`₹${primaryTradingAccountMarginAvailable.toLocaleString('en-IN')}`}
                                                </Typography>
                                            ) : (
                                                <Tooltip title={`${primaryTradingAccount ? 'Reconnect trading account' : 'Connect a trading account'} to view available margin`} arrow placement="top">
                                                    <IconButton
                                                        sx={{
                                                            p: 0,
                                                            m: 0,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={handleAllMarginsRefresh}
                                                    >
                                                        <RefreshIcon sx={{ width: 16, height: 16, stroke: blue }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {isAccountActive && (
                                                <IconButton
                                                    sx={{
                                                        p: 0,
                                                        m: 0,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                    onClick={handleAllMarginsRefresh}
                                                >
                                                    <RefreshIcon sx={{ width: 16, height: 16, stroke: blue }} />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </Stack>
                                )
                            })()}
                        </Stack>
                        <Stack
                            gap={1}
                            direction={{
                                xs: 'row-reverse',
                                sm: 'row'
                            }}
                            sx={{
                                flexShrink: 0,
                                alignSelf: { xs: 'flex-end', sm: 'center' }
                            }}
                        >
                            <Button data-event={'Cancel Clicked'} data-event-source={'quick-trade-window'} size="small"
                                onClick={onClose} variant="outlined">
                                Cancel
                            </Button>
                            <SurveillanceWarningDialog
                                open={showSurveillanceDialog}
                                info={surveillanceInfo}
                                onClose={() => setShowSurveillanceDialog(false)}
                                onConfirm={() => {
                                    setConfirmedFromSurveillance(true);
                                    setShowSurveillanceDialog(false);
                                }}
                            />
                            <LoadingButton
                                data-event={`${transactionType === TransactionType.BUY ? 'Buy' : 'Sell'} Clicked`}
                                data-event-source={'quick-trade-window'} size="small" onClick={handlePlaceOrder}
                                variant="contained"
                                loading={requestInProgres}>
                                {
                                    transactionType === TransactionType.BUY ? !showAllTradeOptions ? 'Practice Buy' : 'Buy' : !showAllTradeOptions ? 'Practice Sell' : 'Sell'
                                }
                            </LoadingButton>
                        </Stack>
                    </Stack>
                </DialogActions>
            </Paper>
        </Fade>
    )
}

export default memo(QuickTradeWindowV2)